import Immutable from 'immutable';

import { momentInZone } from 'util/dateTime';
import { getUrlParamValue } from 'util/urlParams';

export const payrollDetails = (payrollSegment: Immutable.Map<any, any>) => {
  const queryStringProvider = getUrlParamValue('provider');
  const queryStringSegment = getUrlParamValue('method');

  const payrollProvider =
    payrollSegment.get('most_recent_provider') || queryStringProvider || '';

  const payrollMethod =
    payrollSegment.get('most_recent_method') || queryStringSegment || 'default';

  const payrollMethodDetails = payrollMethod.split(' ');

  const companySegment = payrollMethodDetails.slice(-1)[0].toLowerCase();
  const companySegmentType =
    payrollMethodDetails.length > 1
      ? payrollMethodDetails[0].toLowerCase()
      : '';

  return { payrollProvider, companySegment, companySegmentType };
};

export const isWeekend = (day: moment.Moment): boolean => {
  const dayOfWeek = day.day();
  return dayOfWeek === 0 || dayOfWeek === 6;
};

// For the payroll start date component, on the date picker,
// we should block at least 3 business days from the current day.
// i.e if it's on a Monday they user should be able to select from Thursday.
// if it's Friday, the user should be able to select a date from Wednesday of the next week
export const minimumBusinessDaysForFirstPayDate = () => {
  // this experiment is only for inception users so we'll have 3 here as the default
  const minimumDaysForFirstPayDate = 3;
  let numberOfDays = 0;
  let businessDays = 0;
  const targetBusinessDaysCount = minimumDaysForFirstPayDate;

  while (businessDays < targetBusinessDaysCount) {
    const checkDate = momentInZone().add(numberOfDays, 'days');
    if (!isWeekend(checkDate)) {
      businessDays += 1;
    }
    numberOfDays += 1;
  }

  return numberOfDays;
};

export const generateRandomPin = (pinLength: number): string => {
  const max = 10 ** pinLength;
  const num = Math.floor(Math.random() * max);
  return num.toString().padStart(pinLength, '0');
};

// Reusable retry helper
export const apiCallWithRetry = async <T>(
  apiCallback: () => Promise<T>,
  retries = 3,
  delay = 1000
): Promise<T> => {
  let attempt = 0;
  while (attempt < retries) {
    try {
      // Attempt the API call
      // eslint-disable-next-line no-await-in-loop
      const result = await apiCallback();

      // If result is falsy or an empty object/array, treat as failure
      const isEmptyObject =
        typeof result === 'object' &&
        result !== null &&
        !Array.isArray(result) &&
        Object.keys(result as Record<string, unknown>).length === 0;

      const isEmptyArray = Array.isArray(result) && result.length === 0;

      if (result && !isEmptyObject && !isEmptyArray) {
        return result;
      }

      throw new Error('Empty response');
    } catch (error) {
      attempt += 1;
      if (attempt >= retries) {
        // Throw error after max retries
        throw error;
      }

      // Delay before retry
      // eslint-disable-next-line no-await-in-loop
      await new Promise(res => setTimeout(res, delay));
    }
  }
  // This line should never be reached but satisfies TypeScript
  throw new Error('Retry helper reached unreachable code');
};

export const delay = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));
